// Advanced Monitoring for OCR and Worker Performance
export interface OCRMetrics {
  totalProcessed: number;
  successfulExtractions: number;
  averageConfidence: number;
  processingTimes: number[];
  accuracyByType: {
    amount: { correct: number; total: number };
    phone: { correct: number; total: number };
    reference: { correct: number; total: number };
  };
  errorTypes: { [key: string]: number };
  languagePerformance: {
    [language: string]: {
      accuracy: number;
      averageConfidence: number;
      processingTime: number;
    };
  };
}

export interface WorkerMetrics {
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  averageProcessingTime: number;
  queueWaitTime: number;
  throughput: number; // tasks per minute
  errorRate: number;
  circuitBreakerTrips: number;
  memoryUsage: number;
  cpuUsage: number;
}

export interface AlertLevel {
  level: 'info' | 'warning' | 'error' | 'critical';
  threshold: number;
  message: string;
}

export interface PerformanceThresholds {
  ocr: {
    minConfidence: number;
    maxProcessingTime: number;
    maxErrorRate: number;
  };
  workers: {
    maxQueueSize: number;
    maxProcessingTime: number;
    maxErrorRate: number;
    maxMemoryUsage: number;
  };
  promptpay: {
    maxResponseTime: number;
    maxFailureRate: number;
    minSuccessRate: number;
  };
}

export class AutomationMonitoringService {
  private ocrMetrics: OCRMetrics = {
    totalProcessed: 0,
    successfulExtractions: 0,
    averageConfidence: 0,
    processingTimes: [],
    accuracyByType: {
      amount: { correct: 0, total: 0 },
      phone: { correct: 0, total: 0 },
      reference: { correct: 0, total: 0 }
    },
    errorTypes: {},
    languagePerformance: {}
  };

  private workerMetrics: WorkerMetrics = {
    totalTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    averageProcessingTime: 0,
    queueWaitTime: 0,
    throughput: 0,
    errorRate: 0,
    circuitBreakerTrips: 0,
    memoryUsage: 0,
    cpuUsage: 0
  };

  private thresholds: PerformanceThresholds = {
    ocr: {
      minConfidence: 70,
      maxProcessingTime: 10000, // 10 seconds
      maxErrorRate: 0.15 // 15%
    },
    workers: {
      maxQueueSize: 100,
      maxProcessingTime: 30000, // 30 seconds
      maxErrorRate: 0.10, // 10%
      maxMemoryUsage: 0.80 // 80%
    },
    promptpay: {
      maxResponseTime: 5000, // 5 seconds
      maxFailureRate: 0.05, // 5%
      minSuccessRate: 0.95 // 95%
    }
  };

  private alerts: AlertLevel[] = [];
  private metricsHistory: Array<{
    timestamp: Date;
    ocrMetrics: OCRMetrics;
    workerMetrics: WorkerMetrics;
  }> = [];

  // OCR Monitoring
  recordOCRExtraction(
    confidence: number,
    processingTime: number,
    extractedData: any,
    expectedData: any,
    language: string = 'eng+tha',
    error?: string
  ): void {
    this.ocrMetrics.totalProcessed++;
    this.ocrMetrics.processingTimes.push(processingTime);

    // Keep only last 100 processing times for average calculation
    if (this.ocrMetrics.processingTimes.length > 100) {
      this.ocrMetrics.processingTimes.shift();
    }

    if (error) {
      this.ocrMetrics.errorTypes[error] = (this.ocrMetrics.errorTypes[error] || 0) + 1;
    } else {
      this.ocrMetrics.successfulExtractions++;
      this.ocrMetrics.averageConfidence = 
        (this.ocrMetrics.averageConfidence * (this.ocrMetrics.successfulExtractions - 1) + confidence) / 
        this.ocrMetrics.successfulExtractions;

      // Update language performance
      if (!this.ocrMetrics.languagePerformance[language]) {
        this.ocrMetrics.languagePerformance[language] = {
          accuracy: 0,
          averageConfidence: 0,
          processingTime: 0
        };
      }

      const langPerf = this.ocrMetrics.languagePerformance[language];
      langPerf.averageConfidence = 
        (langPerf.averageConfidence * (this.ocrMetrics.successfulExtractions - 1) + confidence) / 
        this.ocrMetrics.successfulExtractions;
      langPerf.processingTime = 
        (langPerf.processingTime * (this.ocrMetrics.successfulExtractions - 1) + processingTime) / 
        this.ocrMetrics.successfulExtractions;

      // Validate extraction accuracy
      if (expectedData) {
        this.validateExtractionAccuracy(extractedData, expectedData);
      }
    }

    this.checkOCRThresholds();
    this.logMetrics('OCR', { confidence, processingTime, error });
  }

  private validateExtractionAccuracy(extracted: any, expected: any): void {
    // Amount accuracy
    if (expected.amount !== undefined) {
      this.ocrMetrics.accuracyByType.amount.total++;
      if (Math.abs(extracted.amount - expected.amount) < 0.01) {
        this.ocrMetrics.accuracyByType.amount.correct++;
      }
    }

    // Phone accuracy
    if (expected.phone !== undefined) {
      this.ocrMetrics.accuracyByType.phone.total++;
      if (extracted.phone === expected.phone) {
        this.ocrMetrics.accuracyByType.phone.correct++;
      }
    }

    // Reference accuracy
    if (expected.reference !== undefined) {
      this.ocrMetrics.accuracyByType.reference.total++;
      if (extracted.reference === expected.reference) {
        this.ocrMetrics.accuracyByType.reference.correct++;
      }
    }
  }

  // Worker Monitoring
  recordWorkerTask(
    taskType: string,
    processingTime: number,
    queueWaitTime: number,
    success: boolean,
    memoryUsage?: number,
    cpuUsage?: number
  ): void {
    this.workerMetrics.totalTasks++;

    if (success) {
      this.workerMetrics.completedTasks++;
      this.workerMetrics.averageProcessingTime = 
        (this.workerMetrics.averageProcessingTime * (this.workerMetrics.completedTasks - 1) + processingTime) / 
        this.workerMetrics.completedTasks;
      this.workerMetrics.queueWaitTime = 
        (this.workerMetrics.queueWaitTime * (this.workerMetrics.completedTasks - 1) + queueWaitTime) / 
        this.workerMetrics.completedTasks;
    } else {
      this.workerMetrics.failedTasks++;
    }

    if (memoryUsage !== undefined) {
      this.workerMetrics.memoryUsage = Math.max(this.workerMetrics.memoryUsage, memoryUsage);
    }

    if (cpuUsage !== undefined) {
      this.workerMetrics.cpuUsage = Math.max(this.workerMetrics.cpuUsage, cpuUsage);
    }

    this.workerMetrics.errorRate = this.workerMetrics.failedTasks / this.workerMetrics.totalTasks;
    this.workerMetrics.throughput = this.calculateThroughput();

    this.checkWorkerThresholds();
    this.logMetrics('Worker', { taskType, processingTime, queueWaitTime, success });
  }

  recordCircuitBreakerTrip(): void {
    this.workerMetrics.circuitBreakerTrips++;
    this.addAlert('warning', 'Circuit breaker tripped', 'Worker circuit breaker has opened due to high failure rate');
  }

  // PromptPay API Monitoring
  recordPromptPayAPICall(
    endpoint: string,
    responseTime: number,
    success: boolean,
    statusCode?: number
  ): void {
    if (!success) {
      this.addAlert('error', 'PromptPay API failure', `API call to ${endpoint} failed with status ${statusCode}`);
    }

    if (responseTime > this.thresholds.promptpay.maxResponseTime) {
      this.addAlert('warning', 'Slow PromptPay API', `Response time ${responseTime}ms exceeds threshold`);
    }

    this.logMetrics('PromptPay', { endpoint, responseTime, success, statusCode });
  }

  // Threshold Monitoring
  private checkOCRThresholds(): void {
    const errorRate = 1 - (this.ocrMetrics.successfulExtractions / this.ocrMetrics.totalProcessed);
    const avgProcessingTime = this.ocrMetrics.processingTimes.length > 0 
      ? this.ocrMetrics.processingTimes.reduce((a, b) => a + b, 0) / this.ocrMetrics.processingTimes.length 
      : 0;

    if (this.ocrMetrics.averageConfidence < this.thresholds.ocr.minConfidence) {
      this.addAlert('warning', 'Low OCR Confidence', 
        `Average confidence ${this.ocrMetrics.averageConfidence}% below threshold`);
    }

    if (avgProcessingTime > this.thresholds.ocr.maxProcessingTime) {
      this.addAlert('warning', 'Slow OCR Processing', 
        `Average processing time ${avgProcessingTime}ms exceeds threshold`);
    }

    if (errorRate > this.thresholds.ocr.maxErrorRate) {
      this.addAlert('error', 'High OCR Error Rate', 
        `Error rate ${(errorRate * 100).toFixed(2)}% exceeds threshold`);
    }
  }

  private checkWorkerThresholds(): void {
    if (this.workerMetrics.errorRate > this.thresholds.workers.maxErrorRate) {
      this.addAlert('error', 'High Worker Error Rate', 
        `Worker error rate ${(this.workerMetrics.errorRate * 100).toFixed(2)}% exceeds threshold`);
    }

    if (this.workerMetrics.averageProcessingTime > this.thresholds.workers.maxProcessingTime) {
      this.addAlert('warning', 'Slow Worker Processing', 
        `Average processing time ${this.workerMetrics.averageProcessingTime}ms exceeds threshold`);
    }

    if (this.workerMetrics.memoryUsage > this.thresholds.workers.maxMemoryUsage) {
      this.addAlert('critical', 'High Memory Usage', 
        `Memory usage ${(this.workerMetrics.memoryUsage * 100).toFixed(2)}% exceeds threshold`);
    }
  }

  // Alert Management
  private addAlert(level: AlertLevel['level'], message: string, details: string): void {
    const alert: AlertLevel = {
      level,
      threshold: 0,
      message
    };

    this.alerts.push(alert);

    // Keep only last 50 alerts
    if (this.alerts.length > 50) {
      this.alerts.shift();
    }
  }

  // Utility Methods
  private calculateThroughput(): number {
    // Calculate tasks per minute based on recent data
    const recentTasks = this.workerMetrics.completedTasks;
    const timeWindow = 60; // 1 minute
    return recentTasks / timeWindow;
  }

  private logMetrics(category: string, data: any): void {
      }

  // Reporting
  generatePerformanceReport(): {
    ocr: OCRMetrics;
    workers: WorkerMetrics;
    alerts: AlertLevel[];
    recommendations: string[];
  } {
    const recommendations = this.generateRecommendations();

    return {
      ocr: { ...this.ocrMetrics },
      workers: { ...this.workerMetrics },
      alerts: [...this.alerts],
      recommendations
    };
  }

  private generateRecommendations(): string[] {
    const recommendations: string[] = [];

    // OCR recommendations
    if (this.ocrMetrics.averageConfidence < 80) {
      recommendations.push('Consider improving image quality or using multiple OCR engines');
    }

    const amountAccuracy = this.ocrMetrics.accuracyByType.amount.total > 0 
      ? (this.ocrMetrics.accuracyByType.amount.correct / this.ocrMetrics.accuracyByType.amount.total) * 100 
      : 0;

    if (amountAccuracy < 90) {
      recommendations.push('Amount extraction accuracy is low, consider improving regex patterns');
    }

    // Worker recommendations
    if (this.workerMetrics.errorRate > 0.05) {
      recommendations.push('High worker error rate detected, review error handling logic');
    }

    if (this.workerMetrics.averageProcessingTime > 5000) {
      recommendations.push('Worker processing time is high, consider optimizing task execution');
    }

    return recommendations;
  }

  // Health Check
  performHealthCheck(): {
    status: 'healthy' | 'degraded' | 'unhealthy';
    checks: {
      ocr: boolean;
      workers: boolean;
      alerts: boolean;
    };
    issues: string[];
  } {
    const checks = {
      ocr: this.ocrMetrics.averageConfidence >= this.thresholds.ocr.minConfidence,
      workers: this.workerMetrics.errorRate <= this.thresholds.workers.maxErrorRate,
      alerts: this.alerts.filter(a => a.level === 'critical').length === 0
    };

    const issues: string[] = [];
    if (!checks.ocr) issues.push('OCR confidence below threshold');
    if (!checks.workers) issues.push('Worker error rate above threshold');
    if (!checks.alerts) issues.push('Critical alerts present');

    const allPassed = Object.values(checks).every(Boolean);
    const someFailed = Object.values(checks).some(c => !c);

    return {
      status: allPassed ? 'healthy' : someFailed ? 'degraded' : 'unhealthy',
      checks,
      issues
    };
  }

  // Metrics History
  saveMetricsSnapshot(): void {
    this.metricsHistory.push({
      timestamp: new Date(),
      ocrMetrics: { ...this.ocrMetrics },
      workerMetrics: { ...this.workerMetrics }
    });

    // Keep only last 24 hours of snapshots (assuming saved every 5 minutes)
    if (this.metricsHistory.length > 288) {
      this.metricsHistory.shift();
    }
  }

  getMetricsHistory(hours: number = 24): Array<{
    timestamp: Date;
    ocrMetrics: OCRMetrics;
    workerMetrics: WorkerMetrics;
  }> {
    const cutoffTime = new Date(Date.now() - hours * 60 * 60 * 1000);
    return this.metricsHistory.filter(snapshot => snapshot.timestamp >= cutoffTime);
  }

  // Getters for current metrics
  getOCRMetrics(): OCRMetrics {
    return { ...this.ocrMetrics };
  }

  getWorkerMetrics(): WorkerMetrics {
    return { ...this.workerMetrics };
  }

  getAlerts(): AlertLevel[] {
    return [...this.alerts];
  }
}

// Singleton instance
export const automationMonitoring = new AutomationMonitoringService();
